import { Plus, Trash2 } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button, DataTable, type DataTableColumnVisibility, type DataTableProps, formatAmount } from '@column/ui';

import { defaultACHColumns, useACHTransfersEditTable } from '../_utils/ach';
import { defaultBookColumns, useBookTransfersEditTable } from '../_utils/book';
import { TEMPLATE_TABLE_CLASS_NAME, withAmountFooter } from '../_utils/shared';
import { defaultWireColumns, useWireTransfersEditTable } from '../_utils/wire';
import { TransferTemplateForm } from '../types';
import { useDialog } from '~/components/Dialog/Provider';
import {
  AchTransferTemplateResponse,
  BookTransferTemplateResponse,
  TransferTemplateType,
  WireTransferTemplateResponse,
} from '~/repositories/Transfer/TransferTemplateRepository';

interface EditTemplateTableProps {
  type: TransferTemplateType;
}

export const EditTemplateTable: FC<EditTemplateTableProps> = ({ type }) => {
  const { openAlert } = useDialog();
  const { setValue } = useFormContext<TransferTemplateForm>();

  useEffect(() => {
    switch (type) {
      case TransferTemplateType.ACH:
        setValue('visibleColumns', defaultACHColumns);
        break;
      case TransferTemplateType.Wire:
        setValue('visibleColumns', defaultWireColumns);
        break;
      case TransferTemplateType.Book:
        setValue('visibleColumns', defaultBookColumns);
        break;
    }
  }, [type, setValue]);

  const {
    append: achAppend,
    remove: achRemove,
    data: achTransfers,
    columns: achColumns,
    columnVisibility: achColumnVisibility,
  } = useACHTransfersEditTable();

  const {
    append: wireAppend,
    remove: wireRemove,
    data: wireTransfers,
    columns: wireColumns,
    columnVisibility: wireColumnVisibility,
  } = useWireTransfersEditTable();

  const {
    append: bookAppend,
    data: bookTransfers,
    columns: bookColumns,
    columnVisibility: bookColumnVisibility,
    remove: bookRemove,
  } = useBookTransfersEditTable();

  const { total, count } = useMemo(() => {
    const rows =
      type === TransferTemplateType.ACH
        ? achTransfers
        : type === TransferTemplateType.Wire
          ? wireTransfers
          : bookTransfers;
    return {
      total: (rows ?? []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
      count: rows?.length ?? 0,
    };
  }, [type, achTransfers, wireTransfers, bookTransfers]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusNewRow = useRef(false);

  // New rows are appended last, so the last amount input is the new row's.
  useEffect(() => {
    if (!focusNewRow.current) {
      return;
    }
    focusNewRow.current = false;
    const amountInputs = wrapperRef.current?.querySelectorAll<HTMLInputElement>(
      '[data-slot="data-table-amount-input"]'
    );
    amountInputs?.[amountInputs.length - 1]?.focus();
  }, [count]);

  const handleAddClick = useCallback(() => {
    focusNewRow.current = true;
    if (type === TransferTemplateType.ACH) {
      achAppend();
    }
    if (type === TransferTemplateType.Wire) {
      wireAppend();
    }
    if (type === TransferTemplateType.Book) {
      bookAppend();
    }
  }, [type, achAppend, wireAppend, bookAppend]);

  const handleDeleteClick = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }

      openAlert({
        headline: 'Remove transfer?',
        content: 'Are you sure you want to remove this transfer and the related data?',
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => {
          if (type === TransferTemplateType.ACH) {
            achRemove(id);
          } else if (type === TransferTemplateType.Wire) {
            wireRemove(id);
          } else if (type === TransferTemplateType.Book) {
            bookRemove(id);
          }
        },
      });
    },
    [type, achRemove, wireRemove, bookRemove, openAlert]
  );

  const handleColumnVisibilityChange = useCallback(
    (visibilityState: DataTableColumnVisibility) => {
      setValue(
        'visibleColumns',
        Object.entries(visibilityState)
          .filter(([, visible]) => visible)
          .map(([id]) => id)
      );
    },
    [setValue]
  );

  const columnVisibility =
    type === TransferTemplateType.ACH
      ? achColumnVisibility
      : type === TransferTemplateType.Wire
        ? wireColumnVisibility
        : bookColumnVisibility;
  const showTotal = count > 1 && (columnVisibility as Record<string, boolean>).amount !== false;
  const totalFooter = showTotal ? (
    <span className="font-medium text-foreground tabular-nums">{formatAmount(total, 'USD')}</span>
  ) : undefined;

  const tableProps: Pick<
    DataTableProps<AchTransferTemplateResponse | WireTransferTemplateResponse | BookTransferTemplateResponse>,
    'className' | 'emptyMessage' | 'footer' | 'rowKey' | 'rowAction'
  > = {
    className: TEMPLATE_TABLE_CLASS_NAME,
    emptyMessage: 'No entries',
    footer: showTotal ? 'Total' : undefined,
    rowKey: (row) => row.transferTemplateRowId ?? '',
    rowAction: (row) => (
      <Button
        type="button"
        size="icon-xs"
        variant="muted"
        aria-label="Remove transfer"
        onClick={() => handleDeleteClick(row.transferTemplateRowId)}
      >
        <Trash2 />
      </Button>
    ),
  };

  return (
    <div ref={wrapperRef} className="grid gap-2">
      {type === TransferTemplateType.ACH && (
        <DataTable<AchTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(achColumns, totalFooter)}
          columnVisibility={achColumnVisibility}
          data={achTransfers}
          headerAction={
            <DataTable.ColumnSelector<AchTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={achColumns}
              onValueChange={handleColumnVisibilityChange}
              value={achColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Wire && (
        <DataTable<WireTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(wireColumns, totalFooter)}
          columnVisibility={wireColumnVisibility}
          data={wireTransfers}
          headerAction={
            <DataTable.ColumnSelector<WireTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={wireColumns}
              onValueChange={handleColumnVisibilityChange}
              value={wireColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Book && (
        <DataTable<BookTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(bookColumns, totalFooter)}
          columnVisibility={bookColumnVisibility}
          data={bookTransfers}
          headerAction={
            <DataTable.ColumnSelector<BookTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={bookColumns}
              onValueChange={handleColumnVisibilityChange}
              value={bookColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      <Button
        type="button"
        size="sm"
        variant="muted"
        aria-label="Add row"
        className="w-full bg-gray-25 hover:bg-gray-50 dark:bg-white/[0.02] dark:hover:bg-white/5"
        onClick={handleAddClick}
      >
        <Plus />
      </Button>
    </div>
  );
};
